/** @jsxImportSource @emotion/react */
import { useCallback, useMemo, useState } from 'react'
import { Button, Card, Form, Spinner } from 'react-bootstrap'
import { skipToken } from '@reduxjs/toolkit/query'
import { useDebounce } from 'use-debounce'
import moment from 'moment'
import Select from 'react-select'
import { Link, useNavigate } from 'react-router-dom'
import { formalName, nc } from 'helpers/nameHelper'

import DataTable from 'components/atoms/DataTable'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { Physician, ProviderType } from 'store/types'
import { useGetLabOfficesQuery } from 'store/services/office'
import {
  useExportFollowUpCareReportMutation,
  useGetFollowUpCareReportQuery,
} from 'store/services/managementReports'
import {
  useGetFollowUpPhysiciansQuery,
  useGetLabPhysiciansQuery,
} from 'store/services/labPhysician'

type Option<T extends string | number> = {
  label: string
  value: T
}

type AppliedFilters = {
  officeId?: number
  referringProviderId?: number
  followUpProviderId?: number
  status?: 'SCHEDULED' | 'CLOSED'
  followUpProviderType?: 'SPECIALIST' | 'DME'
  patientFirstName?: string
  patientLastName?: string
}

const PAGE_SIZE_DEFAULT = 20

const statusOptions: Option<'SCHEDULED' | 'CLOSED'>[] = [
  { label: 'Scheduled', value: 'SCHEDULED' },
  { label: 'Closed', value: 'CLOSED' },
]

const followUpProviderTypeOptions: Option<'SPECIALIST' | 'DME'>[] = [
  { label: 'Specialist', value: 'SPECIALIST' },
  { label: 'DME', value: 'DME' },
]

const statusLabelMap = {
  SCHEDULED: 'Scheduled',
  CLOSED: 'Closed',
}

const providerTypeLabelMap = {
  SPECIALIST: 'Specialist',
  DME: 'DME',
}

const formatDate = (value?: string | Date | null) =>
  value ? moment(value).format('MM/DD/YYYY') : '—'

// Date-only value stored as UTC midnight; read it in UTC so it doesn't shift to the previous day.
const formatDateOnly = (value?: string | Date | null) =>
  value ? moment.utc(value).format('MM/DD/YYYY') : '—'

const formatPhysicianName = (physician: Physician) => {
  if (physician.providerType !== ProviderType.INDIVIDUAL && physician.organizationName) {
    return physician.organizationName
  }

  return formalName(physician.firstName, physician.lastName) || nc(physician.lastName) || nc(physician.firstName)
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

export const FollowUpCareReports = () => {
  const navigate = useNavigate()
  const [selectedOffice, setSelectedOffice] = useState<Option<number> | null>(null)
  const [selectedReferringProvider, setSelectedReferringProvider] = useState<Option<number> | null>(
    null,
  )
  const [selectedFollowUpProvider, setSelectedFollowUpProvider] = useState<Option<number> | null>(
    null,
  )
  const [selectedStatus, setSelectedStatus] = useState<Option<'SCHEDULED' | 'CLOSED'> | null>(null)
  const [selectedProviderType, setSelectedProviderType] = useState<Option<
    'SPECIALIST' | 'DME'
  > | null>(null)
  const [patientFirstName, setPatientFirstName] = useState('')
  const [patientLastName, setPatientLastName] = useState('')
  const [referringProviderSearch, setReferringProviderSearch] = useState('')
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilters>({})
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT)
  const [debouncedReferringProviderSearch] = useDebounce(referringProviderSearch, 300)

  const { data: offices } = useGetLabOfficesQuery({})
  const { data: followUpPhysicians } = useGetFollowUpPhysiciansQuery({})
  const { data: referringProviders } = useGetLabPhysiciansQuery(
    debouncedReferringProviderSearch.length >= 2
      ? { name: debouncedReferringProviderSearch }
      : skipToken,
  )
  const { data, isFetching } = useGetFollowUpCareReportQuery({
    page: currentPage,
    size: pageSize,
    ...appliedFilters,
  })
  const [exportFollowUpCareReport, { isLoading: isExporting }] =
    useExportFollowUpCareReportMutation()

  const officeOptions = useMemo(
    () =>
      (offices || [])
        .filter((office) => !!office.officeName)
        .sort((left, right) => String(left.officeName).localeCompare(String(right.officeName)))
        .map((office) => ({
          label: String(office.officeName),
          value: Number(office.id),
        })),
    [offices],
  )

  const referringProviderOptions = useMemo(() => {
    const items = Array.isArray(referringProviders) ? referringProviders : []

    return items
      .filter((physician, index, list) => {
        const firstIndex = list.findIndex(
          (candidate) => Number(candidate.id) === Number(physician.id),
        )
        return firstIndex === index
      })
      .map((physician) => ({
        label: formatPhysicianName(physician),
        value: Number(physician.id),
      }))
      .sort((left, right) => left.label.localeCompare(right.label))
  }, [referringProviders])

  const followUpProviderOptions = useMemo(
    () =>
      (followUpPhysicians || [])
        .map((physician) => ({
          label: formatPhysicianName(physician),
          value: Number(physician.id),
        }))
        .sort((left, right) => left.label.localeCompare(right.label)),
    [followUpPhysicians],
  )

  const handleSearch = useCallback(() => {
    setCurrentPage(0)
    setAppliedFilters({
      officeId: selectedOffice?.value,
      referringProviderId: selectedReferringProvider?.value,
      followUpProviderId: selectedFollowUpProvider?.value,
      status: selectedStatus?.value,
      followUpProviderType: selectedProviderType?.value,
      patientFirstName: patientFirstName.trim() || undefined,
      patientLastName: patientLastName.trim() || undefined,
    })
  }, [
    patientFirstName,
    patientLastName,
    selectedFollowUpProvider,
    selectedOffice,
    selectedProviderType,
    selectedReferringProvider,
    selectedStatus,
  ])

  const handleClear = useCallback(() => {
    setSelectedOffice(null)
    setSelectedReferringProvider(null)
    setSelectedFollowUpProvider(null)
    setSelectedStatus(null)
    setSelectedProviderType(null)
    setPatientFirstName('')
    setPatientLastName('')
    setReferringProviderSearch('')
    setCurrentPage(0)
    setAppliedFilters({})
  }, [])

  const handleExport = useCallback(async () => {
    const blob = await exportFollowUpCareReport(appliedFilters).unwrap()
    downloadBlob(blob, `follow-up-care-report-${moment().format('YYYY-MM-DD')}.csv`)
  }, [appliedFilters, exportFollowUpCareReport])

  const tableColumns = useMemo(
    () => [
      {
        Header: 'Order ID',
        accessor: 'id',
        className: 'text-nowrap',
        Cell: ({ value }: { value: number }) => (
          <Link to={`/lab/orderEdit/${value}`} onClick={(event) => event.stopPropagation()}>
            {value}
          </Link>
        ),
      },
      { Header: 'Full Name', accessor: 'patientName' },
      {
        Header: 'Referral Date',
        accessor: 'followUpCareInitiatedAt',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
        className: 'text-nowrap',
      },
      {
        Header: 'Physician Assigned At',
        accessor: 'physicianAssignedAt',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
        className: 'text-nowrap',
      },
      { Header: 'Referring Provider', accessor: 'referringProviderName' },
      { Header: 'Follow-up Provider', accessor: 'followUpProviderName' },
      {
        Header: 'Days Since Referral',
        accessor: 'daysSinceReferral',
        Cell: ({ value }: { value?: number | null }) => value ?? '—',
      },
      {
        Header: 'Days To Confirmation',
        accessor: 'daysToConfirmation',
        Cell: ({ value }: { value?: number | null }) => value ?? '—',
      },
      {
        Header: 'Referral Sent Date',
        accessor: 'referralSentDate',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
        className: 'text-nowrap',
      },
      {
        Header: 'Confirmed Receipt Date',
        accessor: 'confirmedReceiptDate',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
        className: 'text-nowrap',
      },
      {
        Header: 'Closed Date',
        accessor: 'closedDate',
        Cell: ({ value }: { value?: string | null }) => formatDate(value),
        className: 'text-nowrap',
      },
      {
        Header: 'Scheduled Date',
        accessor: 'scheduledDate',
        Cell: ({ value }: { value?: string | null }) => formatDateOnly(value),
        className: 'text-nowrap',
      },
      {
        Header: 'Follow-up Provider Type',
        accessor: 'followUpProviderType',
        Cell: ({ value }: { value: 'SPECIALIST' | 'DME' }) => providerTypeLabelMap[value] || value,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }: { value: 'SCHEDULED' | 'CLOSED' }) => statusLabelMap[value] || value,
      },
    ],
    [],
  )

  const tableData = useMemo(
    () => ({
      items: data?.orders?.items || [],
      currentPage: data?.orders?.currentPage ?? currentPage,
      totalItems: data?.orders?.totalItems ?? 0,
    }),
    [currentPage, data],
  )

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">Follow-up Care Reports</h4>
                {(isFetching || isExporting) && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Office</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    value={selectedOffice}
                    onChange={(option) => setSelectedOffice(option as Option<number> | null)}
                    options={officeOptions}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Referral Provider</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    placeholder="Search by name..."
                    value={selectedReferringProvider}
                    onInputChange={(value) => setReferringProviderSearch(value)}
                    onChange={(option) =>
                      setSelectedReferringProvider(option as Option<number> | null)
                    }
                    options={referringProviderOptions}
                    noOptionsMessage={() =>
                      debouncedReferringProviderSearch.length < 2
                        ? 'Type at least 2 characters...'
                        : 'No providers found'
                    }
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Follow-up Provider</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    value={selectedFollowUpProvider}
                    onChange={(option) =>
                      setSelectedFollowUpProvider(option as Option<number> | null)
                    }
                    options={followUpProviderOptions}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Status</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    value={selectedStatus}
                    onChange={(option) =>
                      setSelectedStatus(option as Option<'SCHEDULED' | 'CLOSED'> | null)
                    }
                    options={statusOptions}
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Follow-up Provider Type</label>
                  <Select
                    classNamePrefix="react-select"
                    isClearable
                    value={selectedProviderType}
                    onChange={(option) =>
                      setSelectedProviderType(option as Option<'SPECIALIST' | 'DME'> | null)
                    }
                    options={followUpProviderTypeOptions}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Patient First Name</label>
                  <Form.Control
                    type="text"
                    value={patientFirstName}
                    onChange={(event) => setPatientFirstName(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2">
                  <label className="mb-1">Patient Last Name</label>
                  <Form.Control
                    type="text"
                    value={patientLastName}
                    onChange={(event) => setPatientLastName(event.target.value)}
                    onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="col-md-3 col-sm-12 mb-2 d-flex align-items-end">
                  <Button variant="primary" onClick={handleSearch} disabled={isFetching}>
                    Search
                  </Button>
                  <Button variant="outline-secondary" className="ml-2" onClick={handleClear}>
                    Clear
                  </Button>
                  <Button
                    variant="outline-success"
                    className="ml-2"
                    onClick={handleExport}
                    disabled={isExporting || !tableData.totalItems}>
                    Export CSV
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <Card className="mt-3">
            <Card.Header>
              <div className="d-flex flex-wrap align-items-center justify-content-between">
                <h4 className="mb-0">Orders</h4>
                {isFetching && <Spinner animation="border" size="sm" />}
              </div>
            </Card.Header>
            <Card.Body>
              <TableWrapper
                className="card-body row"
                css={{
                  '& thead': {
                    zIndex: '0 !important',
                  },
                }}>
                <DataTable
                  tableKey="follow_up_care_reports"
                  data={tableData}
                  status={isFetching}
                  tableColumns={tableColumns}
                  pageSize={pageSize}
                  setCurrentPage={setCurrentPage}
                  currentPage={currentPage}
                  setPageSize={setPageSize}
                  setSortOptions={() => {}}
                  searchable={false}
                  entityName="orders"
                  onRowClick={(row) => {
                    navigate(`/lab/orderEdit/${row.original.id}`)
                  }}
                />
              </TableWrapper>
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default FollowUpCareReports
